import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

interface Product {
  _id: string;
  name: string;
  slug: string;
  shortDescription: string;
  description: string;
  featuredImage?: string;
  pricing: {
    basePrice: number;
    discountPrice?: number;
    chargeTax: boolean;
    inStock: boolean;
  };
  rating?: number;
  organize: {
    category: string;
    subCategory?: string;
    bookType?: string;
  };
}

const ViewProduct: React.FC = () => {
  const { id } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const baseUrl =
    import.meta.env.VITE_API_BASE_URL || "http://localhost:4000/api/v1";

  useEffect(() => {
    if (!id) return;

    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${baseUrl}/products/${id}`);
        if (res.data.status) {
          setProduct(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  if (loading) return <p>Loading...</p>;
  if (!product) return <p>Product not found</p>;

  // calculate discount %
  const discount =
    product.pricing.discountPrice &&
    Math.round(
      ((product.pricing.basePrice - product.pricing.discountPrice) /
        product.pricing.basePrice) *
        100
    );

  // render rating stars (with half star support)
  const renderStars = (rating: number = 0) => {
    const stars = [];
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;

    for (let i = 0; i < full; i++) {
      stars.push(<i key={`full-${i}`} className="fa fa-star text-warning" />);
    }
    if (half) {
      stars.push(<i key="half" className="fa fa-star-half-o text-warning" />);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<i key={`empty-${i}`} className="fa fa-star-o text-muted" />);
    }
    return stars;
  };

  return (
    <div className="main-container container-fluid">
      {/* PAGE HEADER */}
      <div className="page-header">
        <h1 className="page-title">Product Detail</h1>
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="javascript:void(0)">Home</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Product Detail
            </li>
          </ol>
        </div>
      </div>

      <div className="row product-detail-page">
        {/* IMAGE */}
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <div className="product-img-preview">
                <img
                  className="img-fluid"
                  src={product.featuredImage || "/placeholder.png"}
                  alt={product.name}
                />
              </div>
            </div>
          </div>
        </div>

        {/* DETAILS */}
        <div className="col-md-7 dashpro-detail">
          <div className="card product-card">
            <div className="card-body">
              <div className="media justify-content-between">
                <div>
                  <h2 className="fw-bold mb-2">{product.name}</h2>

                  {/* short description (HTML from API) */}
                  {product.shortDescription && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: product.shortDescription,
                      }}
                    />
                  )}

                  {/* full description */}
                  {product.description && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: product.description,
                      }}
                    />
                  )}

                  {/* rating */}
                  <ul className="product-rating">{renderStars(product.rating)}</ul>

                  {/* pricing */}
                  <ul className="product-price">
                    <li className="new-price text-primary">
                      ${product.pricing.discountPrice || product.pricing.basePrice}
                    </li>
                    {product.pricing.discountPrice && (
                      <>
                        <li className="old-price fs-15 text-muted fw-400">
                          <del>${product.pricing.basePrice}</del>
                        </li>
                        <li className="ofr-price fs-16">
                          <span className="badge bg-primary-transparent rounded-pill text-primary p-2 px-3">
                            {discount}% off
                          </span>
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>

              {/* quantity selector */}
              <div className="form-group mt-3">
                <label className="form-label">Quantity</label>
                <div className="input-group pro-quantity">
                  <button className="btn btn-outline-default" type="button">
                    <i className="fe fe-minus" />
                  </button>
                  <input
                    className="form-control pro-qty"
                    type="text"
                    name="qty count"
                    defaultValue={1}
                  />
                  <button className="btn btn-outline-default" type="button">
                    <i className="fe fe-plus" />
                  </button>
                </div>
              </div>

              {/* buttons */}
              <div className="mt-3 d-flex gap-3">
                <a className="btn btn-primary fs-16" href="#">
                  <i className="fe fe-shopping-bag me-2" /> Add To Cart
                </a>
                <a className="btn btn-secondary fs-16" href="#">
                  <i className="fe fe-heart me-2" /> Add To Wishlist
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* DESCRIPTION TABS */}
        <div className="col-sm-12 mt-4">
          <div className="card">
            <div className="card-body">
              <div className="product-detail-tab tabs-menu1">
                <ul className="nav panel-tabs" role="tablist">
                  <li>
                    <a
                      href="#tab-1"
                      className="active"
                      data-bs-toggle="tab"
                      aria-selected="true"
                      role="tab"
                    >
                      Description
                    </a>
                  </li>
                </ul>
                <div className="tab-content">
                  <div
                    className="tab-pane fade active show"
                    id="tab-1"
                    role="tabpanel"
                  >
                    {product.description && (
                      <div
                        dangerouslySetInnerHTML={{ __html: product.description }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default ViewProduct;
